import {
  Grid,
  NotificationsIcon,
  Badge,
  IconButton,
  NotificationsOutlinedIcon,
  TIconButtonProps,
} from '@rbx/ui';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@rbx/intl';
import { useLocalStorage } from '@rbx/react-utilities';
import type {
  CreatorStreamNotification,
  CreatorStreamNotificationsGetCreatorStreamNotificationsByUserRequest,
} from '@rbx/clients/creatorNotificationStreamsApi';
import {
  getRealTimeNotificationsBasePath,
  useSignalR,
  type TSignalRCallback,
} from '@rbx/signalr-userhub-client';
import { User } from './types/user';
import useNotificationBellStyles from './NotificationBell.styles';
import {
  NOTIFICATION_CLIENT_PAGE_SIZE,
  NotificationClientProvider,
  useNotificationClient,
} from './contexts/NotificationClientProvider';
import TNotificationGroup from './types/TNotificationGroup';
import NotificationList from './NotificationList';
import CREATOR_HUB_NOTIFICATION_CHANNEL from './utils/CreatorHubNotificationChannel';
import useNavigationConfigs from '../hooks/useNavigationConfigs';
import {
  notificationsReadEventModel,
  openNotificationTrayEventModel,
  closeNotificationTrayEventModel,
  NotificationTrayCloseSource,
  notificationBellIconImpressionEventModel,
} from '../event/eventConstants';
import formatNotifications from './utils/formatNotifications';
import signalREventEmitter from './contexts/SignalREventEmitter';
import { TSendEvent } from '../providers/EventProvider';

export interface TNotificationBellProps {
  user: User | null;
  onSettingsClick?: () => void;
  sendEvent: TSendEvent;
  size?: TIconButtonProps['size'];
  enableNotificationTrayV2?: boolean; // TODO @neoxu(10/23/2025): Remove this once the notification tray v2 is fully released
}

const MAX_NOTIFICATIONS = 150;
const HOURS_IN_MS = 3600000; // 60 * 60 * 1000

const NotificationBell: FunctionComponent<TNotificationBellProps> = ({
  user,
  size = 'large',
  onSettingsClick,
  sendEvent,
  enableNotificationTrayV2,
}) => {
  const { classes: styles, cx } = useNotificationBellStyles();
  const { translate } = useTranslation();
  const { notificationClient } = useNotificationClient();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [newNotificationExists, setNewNotificationExists] = useState(false);
  const [notifications, setNotifications] = useState<TNotificationGroup[]>([]);
  const [lastSeenNotificationId, setLastSeenNotificationId] = useLocalStorage(
    `NotificationTraySeen.${user?.id}`,
    '',
  );
  const [newestNotificationId, setNewestNotificationId] = useState<string>('');
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [loadingTray, setLoadingTray] = useState(false);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [noMoreNotifications, setNoMoreNotifications] = useState(false);
  const [firstNotificationPage, setFirstNotificationPage] = useState<
    CreatorStreamNotification[] | undefined
  >(undefined);
  const [firstTimeLoadingNotificationsFinished, setFirstTimeLoadingNotificationsFinished] = useState<boolean>(false);
  const impressionSentRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const cursorRef = useRef('');
  const syncBroadcastedNotificationsRef = useRef(true);
  const loadingPage = useRef(false);
  const firstNotificationPageViewLogged = useRef(false);
  const prevExpandedRef = useRef<boolean>(false);
  const trayOpenTimestampRef = useRef<number | null>(null);
  const trayCloseSourceRef = useRef<NotificationTrayCloseSource | null>(null);

  const onNewNotification = useCallback((newNotificationId: string) => {
    setNewestNotificationId(newNotificationId);
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((oldNotifications) => {
      const clonedNotificationGroups = oldNotifications.map((n) => {
        const readNotification = structuredClone(n);
        readNotification.titleNotification.read = true;
        readNotification.children = readNotification.children.map(
          (child: CreatorStreamNotification) => {
            const readChildNotification = structuredClone(child);
            readChildNotification.read = true;

            return readChildNotification;
          },
        );

        return readNotification;
      });

      return clonedNotificationGroups;
    });
  }, []);

  const loadPage = useCallback(
    async (isReload: boolean) => {
      if (
        user?.id == null ||
        loadingTray ||
        (noMoreNotifications && !isReload) ||
        loadingPage.current
      ) {
        return;
      }

      try {
        loadingPage.current = true;
        setLoadingTray(true);

        const isFullRefresh = syncBroadcastedNotificationsRef.current;
        const cursor = notifications.length === 0 || isReload ? '' : cursorRef.current;
        const args: CreatorStreamNotificationsGetCreatorStreamNotificationsByUserRequest = {
          userId: user?.id,
          count: NOTIFICATION_CLIENT_PAGE_SIZE,
          notificationChannel: CREATOR_HUB_NOTIFICATION_CHANNEL,
          cursor: cursor || undefined,
          fullRefresh: isFullRefresh || undefined,
        };

        const notificationPage =
          await notificationClient?.creatorStreamNotificationsGetCreatorStreamNotificationsByUser(
            args,
          );

        if (isFullRefresh) {
          syncBroadcastedNotificationsRef.current = false;
        }

        const unformattedCreatorStreamNotifications = notificationPage?.creatorStreamNotifications;
        if (
          !unformattedCreatorStreamNotifications ||
          unformattedCreatorStreamNotifications.length === 0 ||
          notifications.length + unformattedCreatorStreamNotifications.length > MAX_NOTIFICATIONS
        ) {
          setNoMoreNotifications(true);
          return;
        }

        setNoMoreNotifications(false);
        const formattedNotifications = formatNotifications(
          notificationPage?.creatorStreamNotifications,
        );
        cursorRef.current = notificationPage.nextCursor;
        const currentScrollPosition = listRef.current?.scrollTop;

        if (!cursor && formattedNotifications?.[0].titleNotification.notificationId) {
          setNewestNotificationId(formattedNotifications?.[0].titleNotification.notificationId);
        }

        setNotifications([...(isReload ? [] : notifications), ...formattedNotifications]);
        setFailedToLoad(false);
        requestAnimationFrame(() => {
          if (listRef.current && currentScrollPosition) {
            listRef.current.scrollTop = currentScrollPosition;
          }
        });
        if (notifications.length !== 0) {
          sendEvent(notificationsReadEventModel(notificationPage.creatorStreamNotifications));
        } else {
          setFirstNotificationPage(notificationPage.creatorStreamNotifications);
        }
      } catch {
        setFailedToLoad(true);
      } finally {
        loadingPage.current = false;
        setLoadingTray(false);
        setFirstTimeLoadingNotificationsFinished(true);
      }
    },
    [loadingTray, noMoreNotifications, notificationClient, notifications, sendEvent, user?.id],
  );

  useEffect(() => {
    if (expanded && notifications.length > 0) {
      setLastSeenNotificationId(notifications?.[0].titleNotification.notificationId);
    }
  }, [expanded, notifications, setLastSeenNotificationId]);

  useEffect(() => {
    if (!firstNotificationPageViewLogged && expanded && firstNotificationPage) {
      sendEvent(notificationsReadEventModel(firstNotificationPage));
    }
  }, [expanded, firstNotificationPage, sendEvent]);

  useEffect(() => {
    // Track when tray opens
    if (prevExpandedRef.current === false && expanded === true) {
      trayOpenTimestampRef.current = Date.now();
      trayCloseSourceRef.current = null;
    }

    // Send close event when expanded changes from true to false
    if (prevExpandedRef.current === true && expanded === false) {
      const durationMs = trayOpenTimestampRef.current
        ? Date.now() - trayOpenTimestampRef.current
        : undefined;
      // Default to 'clickOutside' if source is not explicitly set
      const source = trayCloseSourceRef.current || NotificationTrayCloseSource.ClickOutside;
      sendEvent(closeNotificationTrayEventModel({
        hasUnSeenNotifications: false, // because tray is closed, then there is no unread notifications
        unreadNotificationCount: notifications.filter((notification) => !notification.titleNotification.read).length,
        notificationsCount: notifications.length,
      }, durationMs, source));
      trayOpenTimestampRef.current = null;
      trayCloseSourceRef.current = null;
    }
    prevExpandedRef.current = expanded;
  }, [expanded, notifications, sendEvent]);

  useEffect(() => {
    setNewNotificationExists(
      !!newestNotificationId && lastSeenNotificationId !== newestNotificationId,
    );
  }, [lastSeenNotificationId, newestNotificationId]);

  // NOTE (@mbae, 03/26/24): Notifications in the backend aren't always up-to-date
  // Broadcasted notifications need to be "pulled in", so the frontend is tasked
  // with telling the backend to occassionally pull broadcasted notifications in
  // for the specific user
  useEffect(() => {
    const interval = setInterval(() => {
      syncBroadcastedNotificationsRef.current = true;
    }, HOURS_IN_MS);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuAnchor) {
        return;
      }

      if (buttonRef.current && buttonRef.current.contains(event.target as Node)) {
        if (newNotificationExists) {
          setNotifications([]);
        }

        setExpanded((isExpanded) => {
          if (!isExpanded) {
            sendEvent(openNotificationTrayEventModel({
              hasUnSeenNotifications: newNotificationExists,
              unreadNotificationCount: notifications.filter((notification) => !notification.titleNotification.read).length,
              notificationsCount: notifications.length,
            }));
            setNewNotificationExists(false);
          } else {
            // Track that closing is from bell icon
            trayCloseSourceRef.current = NotificationTrayCloseSource.BellIcon;
          }

          return !isExpanded;
        });
      } else if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        expanded
      ) {
        setExpanded(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [expanded, menuAnchor, newNotificationExists, notifications, sendEvent]);

  useEffect(() => {
    signalREventEmitter.on('newNotification', onNewNotification);
    return () => {
      signalREventEmitter.removeListener('newNotification', onNewNotification);
    };
  }, [onNewNotification]);

  useEffect(() => {
    if (!failedToLoad && !loadingTray && notifications.length === 0 && !noMoreNotifications) {
      loadPage(true);
    }
  }, [failedToLoad, loadingTray, loadPage, notifications.length, noMoreNotifications]);

  const EntrypointIcon = expanded ? NotificationsIcon : NotificationsOutlinedIcon;

  // NOTE (@neoxu, 11/10/2025)
  // Track notification bell icon impression when tray loading finishes
  // Only execute once after tray finishes rendering
  useEffect(() => {
    // Send impression once when loading finishes
    if (firstTimeLoadingNotificationsFinished && !impressionSentRef.current) {
      const newNotificationId = notifications?.[0]?.titleNotification?.notificationId;
      impressionSentRef.current = true;
      // NOTE (@neoxu, 11/12/2025): Since already has the notifications calculate `hasUnSeenNotifications` to reduce the dependency on `newNotificationExists`
      sendEvent(notificationBellIconImpressionEventModel({
        hasUnSeenNotifications: !!(newNotificationId && lastSeenNotificationId !== newNotificationId),
        unreadNotificationCount: notifications.filter((notification) => !notification.titleNotification.read).length,
        notificationsCount: notifications.length,
      }));
    }
  }, [firstTimeLoadingNotificationsFinished, lastSeenNotificationId, notifications, sendEvent]);

  // NOTE (@neoxu, 11/10/2025)
  // Track notification bell icon impression when new notification indicator appears from hidden to visible
  // trigger conditions:
  // 1. newestNotificationId is not empty. means receiving new notification from signalREventEmitter
  // 2. newNotificationExists is changed from false to true. means new notification indicator appears from hidden to visible
  // 3. notification tray is not expanded. means notification tray is not visible
  const prevNewNotificationExistsRef = useRef<boolean>(false);
  useEffect(() => {
    if (newestNotificationId && newNotificationExists && !expanded && !prevNewNotificationExistsRef.current) {
      sendEvent(notificationBellIconImpressionEventModel({
        hasUnSeenNotifications: newNotificationExists,
        unreadNotificationCount: notifications.filter((notification) => !notification.titleNotification.read).length,
        notificationsCount: notifications.length,
      }));
    }
    
    // Update the previous value for next comparison
    prevNewNotificationExistsRef.current = newNotificationExists;
  }, [newNotificationExists, newestNotificationId, expanded, notifications, sendEvent]);
  
  return (
    <div className={styles.gridContainer}>
      <Grid className={styles.container}>
        <IconButton
          aria-label={`${translate('Heading.Notifications')}`}
          color='secondary'
          size={size}
          ref={buttonRef}
          className={cx(expanded ? styles.expanded : null)}>
          {newNotificationExists && (
            <Badge variant='dot' color='primary'>
              <EntrypointIcon />
            </Badge>
          )}
          {!newNotificationExists && <EntrypointIcon />}
        </IconButton>
      </Grid>
      {expanded && (
        <NotificationList
          ref={containerRef}
          failedToLoad={failedToLoad}
          listRef={listRef}
          loadPage={loadPage}
          loadingTray={loadingTray}
          markAllAsRead={markAllAsRead}
          menuAnchor={menuAnchor}
          newNotificationExists={newNotificationExists}
          noMoreNotifications={noMoreNotifications}
          notifications={notifications}
          setMenuAnchor={setMenuAnchor}
          setNewNotificationExists={setNewNotificationExists}
          setNotifications={setNotifications}
          user={user}
          setExpanded={setExpanded}
          onSettingsClick={onSettingsClick}
          enableNotificationTrayV2={enableNotificationTrayV2}
        />
      )}
    </div>
  );
};

export type TSignalRNotificationMessage = {
  NotificationId?: string;
  Action?: 'MarkRead' | 'MarkAllRead' | 'New';
};

const SignalRInitializer: React.FC<{ children?: React.ReactNode }> = ({
  children: initializerChildren,
}) => {
  const { environment } = useNavigationConfigs();
  const onNotification: TSignalRCallback = useCallback(
    (namespace, detail) => {
      if (namespace === 'CreatorHubTray') {
        const message: TSignalRNotificationMessage = JSON.parse(detail);
        if (!message?.Action) {
          if (environment !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Empty Notification Tray SignalR Message:', detail);
          }

          return;
        }

        if ((!message.Action || message.Action === 'New') && message.NotificationId) {
          signalREventEmitter.emit('newNotification', message.NotificationId);
        } else if (message.Action === 'MarkRead' && message.NotificationId) {
          signalREventEmitter.emit('notificationRead', message.NotificationId);
        } else if (message.Action === 'MarkAllRead') {
          signalREventEmitter.emit('allNotificationsRead');
        } else if (environment !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(`Unhandled Notification Tray message ${message}`);
        }
      }
    },
    [environment],
  );
  const basePath = getRealTimeNotificationsBasePath(environment);
  useSignalR(onNotification, basePath);
  return <React.Fragment>{initializerChildren}</React.Fragment>;
};

const NotificationBellWrapper: FunctionComponent<TNotificationBellProps> = (props) => {
  return (
    <NotificationClientProvider>
      <SignalRInitializer>
        <NotificationBell {...props} />
      </SignalRInitializer>
    </NotificationClientProvider>
  );
};

type TNotificationBellV1Props = Omit<TNotificationBellProps, 'sendEvent'>;
const NotificationBellV1: FunctionComponent<TNotificationBellV1Props> = (props) => {
  const { sendEvent } = useNavigationConfigs();

  return <NotificationBellWrapper sendEvent={sendEvent} {...props} />;
};

export const NotificationBellV2: FunctionComponent<TNotificationBellV1Props> = (props) => {
  const { enableNotificationTrayV2, sendEvent } = useNavigationConfigs();
  return <NotificationBellWrapper sendEvent={sendEvent} enableNotificationTrayV2={enableNotificationTrayV2} {...props} />;
};
export default NotificationBellV1;
